import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PercentIcon from "@mui/icons-material/Percent";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { formatPercentageNoMultiply } from "../../utils/formatUtils";
import { fmtMoneyDetailed } from "../../utils/format";
import {
  averageDailyMargin,
  expandTimeline,
  GoalFinancials,
  platformInversionForAverageMargin,
  platformUnitCostForMargin,
} from "../../utils/marginForecast";
import {
  CampaignMarginPeriod,
  CreateAssociatedCampaignMarginRequest,
  useCreateAssociatedCampaignMarginMutation,
  useDeleteAssociatedCampaignMarginMutation,
  useGetAssociatedCampaignMarginQuery,
  useUpdateAssociatedCampaignMarginMutation,
} from "../../store/apis/associatedCampaignMarginApi";
import AssociatedCampaignMarginDialog from "../dialogs/AssociatedCampaignMarginDialog";

import OrbLoader from "../common/OrbLoader";
import SectionCard from "./inline/SectionCard";
interface AssociatedCampaignMarginSectionProps {
  briefingId: string;
  associatedId: string;
  isEditMode?: boolean;
  // Rango de fechas de la línea (para acotar/validar los DatePicker del diálogo).
  campaignStartDate?: string | null;
  campaignEndDate?: string | null;
  // Cifras del goal para calcular la inversión plataforma con los tramos.
  goal?: GoalFinancials | null;
}

// Extrae el status HTTP y el mensaje de un error de RTK Query.
const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === "object" && error !== null && "status" in error) {
    const s = (error as { status?: unknown }).status;
    return typeof s === "number" ? s : undefined;
  }
  return undefined;
};

const getErrorMessage = (error: unknown): string | null => {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: { message?: string } }).data;
    if (data && typeof data.message === "string") return data.message;
  }
  return null;
};

const AssociatedCampaignMarginSection: React.FC<
  AssociatedCampaignMarginSectionProps
> = ({
  briefingId,
  associatedId,
  isEditMode = false,
  campaignStartDate,
  campaignEndDate,
  goal,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const params = { briefingId, associatedId };
  const { data, isLoading, isFetching, error } =
    useGetAssociatedCampaignMarginQuery(params);

  const [createPeriod, { isLoading: isCreating }] =
    useCreateAssociatedCampaignMarginMutation();
  const [updatePeriod, { isLoading: isUpdating }] =
    useUpdateAssociatedCampaignMarginMutation();
  const [deletePeriod] = useDeleteAssociatedCampaignMarginMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CampaignMarginPeriod | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const history = data?.data ?? null;

  // Margen de cada día según los tramos: base de la inversión actualizada.
  const dailyMargins = useMemo(
    () =>
      history ? expandTimeline(history.timeline) : new Map<string, number>(),
    [history]
  );

  // Inversión y coste unitario plataforma con los tramos guardados, frente al plan.
  const forecast = useMemo(() => {
    const average = averageDailyMargin(dailyMargins);
    if (!goal || average === null) return null;
    const days = Array.from(dailyMargins.keys()).sort();
    const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
    const from =
      tomorrow < days[0]
        ? days[0]
        : tomorrow > days[days.length - 1]
          ? null
          : tomorrow;
    const forwardMargin =
      dailyMargins.get(from ?? days[days.length - 1]) ?? goal.margin;
    return {
      average,
      forwardMargin,
      from,
      platformInversion: platformInversionForAverageMargin(goal, average),
      netUnitCost: platformUnitCostForMargin(goal, forwardMargin),
    };
  }, [dailyMargins, goal]);

  const openCreate = (): void => {
    setEditing(null);
    setServerError(null);
    setDialogOpen(true);
  };

  const openEdit = (period: CampaignMarginPeriod): void => {
    setEditing(period);
    setServerError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (
    body: CreateAssociatedCampaignMarginRequest
  ): Promise<void> => {
    setServerError(null);
    try {
      if (editing) {
        await updatePeriod({ params, periodId: editing.id, body }).unwrap();
      } else {
        await createPeriod({ params, body }).unwrap();
      }
      dispatch(
        setToastMessage({
          message: t("briefingsView.marginSection.saveSuccess", "Margin saved"),
          severity: "success",
        })
      );
      setDialogOpen(false);
    } catch (err) {
      setServerError(
        getErrorMessage(err) ||
          t("briefingsView.marginSection.saveError", "Failed to save margin")
      );
    }
  };

  const handleDelete = async (period: CampaignMarginPeriod): Promise<void> => {
    try {
      await deletePeriod({ params, periodId: period.id }).unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.marginSection.deleteSuccess",
            "Margin period deleted"
          ),
          severity: "success",
        })
      );
    } catch (err) {
      dispatch(
        setToastMessage({
          message:
            getErrorMessage(err) ||
            t(
              "briefingsView.marginSection.deleteError",
              "Failed to delete margin period"
            ),
          severity: "error",
        })
      );
    }
  };

  const renderBody = (): React.ReactNode => {
    if (isLoading || isFetching) {
      return (
        <Stack direction="row" justifyContent="center" py={4}>
          <OrbLoader size="large" />
        </Stack>
      );
    }

    if (error) {
      const status = getErrorStatus(error);
      // 409: la línea aún no está sincronizada a producción.
      if (status === 409) {
        return (
          <Alert severity="info">
            {t(
              "briefingsView.marginSection.notSynced",
              "This line is not active in production yet."
            )}
          </Alert>
        );
      }
      if (status === 403) {
        return (
          <Alert severity="warning">
            {t("briefingsView.marginSection.forbidden", "Access denied.")}
          </Alert>
        );
      }
      return (
        <Alert severity="error">
          {t(
            "briefingsView.marginSection.loadError",
            "Unable to load margin information."
          )}
        </Alert>
      );
    }

    if (!history) return null;

    return (
      <Stack spacing={3}>
        {/* Margen por defecto de la línea (del Goal) */}
        <Box>
          <Typography variant="caption" color={theme.palette.neutral[600]}>
            {t("briefingsView.marginSection.defaultMargin", "Default margin")}
          </Typography>
          <Box>
            <Chip
              icon={<PercentIcon />}
              label={formatPercentageNoMultiply(history.defaultMargin)}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Impacto de los tramos en la compra en plataforma */}
        {goal && forecast && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            {[
              {
                label: t(
                  "briefingsView.marginSection.platformInversion",
                  "Inversión plataforma prevista"
                ),
                plan: fmtMoneyDetailed(goal.platformInversion, 2),
                updated: fmtMoneyDetailed(forecast.platformInversion, 2),
                hint: t(
                  "briefingsView.marginSection.platformInversionHint",
                  "Toda la campaña, con el margen de cada día (margen medio {{margin}})",
                  { margin: formatPercentageNoMultiply(forecast.average) }
                ),
              },
              {
                label: t(
                  "briefingsView.marginSection.platformUnitCost",
                  "Coste unitario plataforma"
                ),
                plan: fmtMoneyDetailed(goal.netUnitCost),
                updated: fmtMoneyDetailed(forecast.netUnitCost),
                hint: forecast.from
                  ? t(
                      "briefingsView.marginSection.platformUnitCostHint",
                      "A cuánto comprar desde el {{date}} (margen {{margin}})",
                      {
                        date: dayjs(forecast.from).format("DD/MM/YYYY"),
                        margin: formatPercentageNoMultiply(
                          forecast.forwardMargin
                        ),
                      }
                    )
                  : t(
                      "briefingsView.marginSection.platformUnitCostEndedHint",
                      "Campaña terminada: margen del último día ({{margin}})",
                      {
                        margin: formatPercentageNoMultiply(
                          forecast.forwardMargin
                        ),
                      }
                    ),
              },
            ].map((card) => (
              <Box
                key={card.label}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Typography
                  variant="caption"
                  color={theme.palette.neutral[600]}
                >
                  {card.label}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="baseline">
                  <Typography variant="h6" fontWeight={700}>
                    {card.updated}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={theme.palette.neutral[600]}
                  >
                    {t("briefingsView.marginSection.plan", "Plan")}: {card.plan}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color={theme.palette.neutral[600]}
                >
                  {card.hint}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Timeline consolidada día a día */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("briefingsView.marginSection.timelineTitle", "Margin timeline")}
          </Typography>
          {history.timeline.length === 0 ? (
            <Typography variant="caption" color={theme.palette.neutral[600]}>
              {t("briefingsView.marginSection.noTimeline", "No margin data.")}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("metrics.marginTable.startDate", "Start Date")}
                    </TableCell>
                    <TableCell>
                      {t("metrics.marginTable.endDate", "End Date")}
                    </TableCell>
                    <TableCell align="right">
                      {t("metrics.marginTable.margin", "Margin")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.timeline.map((p, idx) => (
                    <TableRow key={`${p.startDate}-${p.endDate}-${idx}`}>
                      <TableCell>
                        {dayjs(p.startDate).format("YYYY-MM-DD")}
                      </TableCell>
                      <TableCell>
                        {dayjs(p.endDate).format("YYYY-MM-DD")}
                      </TableCell>
                      <TableCell align="right">
                        {formatPercentageNoMultiply(p.margin)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Tramos editables */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("briefingsView.marginSection.periodsTitle", "Margin periods")}
          </Typography>
          {history.periods.length === 0 ? (
            <Typography variant="caption" color={theme.palette.neutral[600]}>
              {t(
                "briefingsView.marginSection.noPeriods",
                "No margin periods defined. The default margin applies."
              )}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right">
                      {t("metrics.marginTable.margin", "Margin")}
                    </TableCell>
                    <TableCell>
                      {t(
                        "briefingsView.marginSection.description",
                        "Description"
                      )}
                    </TableCell>
                    <TableCell>
                      {t("metrics.marginTable.startDate", "Start Date")}
                    </TableCell>
                    <TableCell>
                      {t("metrics.marginTable.endDate", "End Date")}
                    </TableCell>
                    {isEditMode && (
                      <TableCell align="right">
                        {t("common.actions", "Actions")}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.periods.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell align="right">
                        {formatPercentageNoMultiply(p.margin)}
                      </TableCell>
                      <TableCell>{p.description || "—"}</TableCell>
                      <TableCell>
                        {dayjs(p.startDate).format("YYYY-MM-DD")}
                      </TableCell>
                      <TableCell>
                        {dayjs(p.endDate).format("YYYY-MM-DD")}
                      </TableCell>
                      {isEditMode && (
                        <TableCell align="right">
                          <Tooltip title={t("common.edit", "Edit")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEdit(p)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common.delete", "Delete")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(p)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Stack>
    );
  };

  // El botón de añadir tramo solo si hay historial cargado (línea sincronizada).
  const canAddPeriod = isEditMode && !error && !!history;

  return (
    <SectionCard
      icon={<PercentIcon />}
      title={t("briefingsView.marginSection.title", "Margin")}
      subtitle={t(
        "briefingsView.marginSection.subtitle",
        "Tramos de margen y su impacto en la compra"
      )}
      actions={
        canAddPeriod && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            {t("briefingsView.marginSection.addPeriod", "Add margin period")}
          </Button>
        )
      }
    >
      {renderBody()}

      {dialogOpen && (
        <AssociatedCampaignMarginDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
          period={editing}
          isLoading={isCreating || isUpdating}
          serverError={serverError}
          campaignStartDate={campaignStartDate}
          campaignEndDate={campaignEndDate}
          briefingId={briefingId}
          associatedId={associatedId}
          goal={goal ?? null}
          dailyMargins={dailyMargins}
        />
      )}
    </SectionCard>
  );
};

export default AssociatedCampaignMarginSection;
